// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * InventoryOverview main container component.
 * Renders the inventory overview from data the route has already fetched and
 * transformed, so the page issues a single inventory request.
 */
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { AssetStatusLegend } from '@/inventory/components/overview/components/asset-status-legend';
import { InventoryOverviewGrid } from '@/inventory/components/overview/inventory-overview-grid';
import { containerStyles } from '@/inventory/components/overview/styles';
import { InventoryOverviewData } from '@/inventory/components/overview/types';
import { Box } from '@mui/material';
import { useCallback } from 'react';

interface InventoryOverviewProps {
  /**
   * The transformed overview table data
   */
  data: InventoryOverviewData;
  /**
   * Callback when user clicks on asset counts to drill down
   */
  onAssetClick?: (
    productKey: string,
    locationKey: string,
    status: AssetFunctionalStatus
  ) => void;
  /**
   * Whether to show scrapped assets in status indicators
   */
  showScrappedAssets?: boolean;
  /**
   * Whether to hide zero values instead of graying them out
   */
  hideZeroes?: boolean;
  /**
   * Collapsed state for inventory sections
   */
  isInventoryExpanded: boolean;
  isNonInventoryExpanded: boolean;
  isInTransitExpanded: boolean;
  onToggleInventory: () => void;
  onToggleNonInventory: () => void;
  onToggleInTransit: () => void;
}

/**
 * InventoryOverview main container component
 */
export function InventoryOverview(props: InventoryOverviewProps) {
  // Handle asset click events
  const handleAssetClick = useCallback(
    (
      productKey: string,
      locationKey: string,
      status: AssetFunctionalStatus
    ) => {
      if (props.onAssetClick) {
        props.onAssetClick(productKey, locationKey, status);
      }
    },
    [props]
  );

  return (
    <Box sx={containerStyles.main}>
      <InventoryOverviewGrid
        data={props.data}
        onAssetClick={handleAssetClick}
        showScrappedAssets={props.showScrappedAssets ?? false}
        hideZeroes={props.hideZeroes ?? false}
        isInventoryExpanded={props.isInventoryExpanded}
        isNonInventoryExpanded={props.isNonInventoryExpanded}
        isInTransitExpanded={props.isInTransitExpanded}
        onToggleInventory={props.onToggleInventory}
        onToggleNonInventory={props.onToggleNonInventory}
        onToggleInTransit={props.onToggleInTransit}
      />
      <Box sx={{ paddingX: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <AssetStatusLegend
          showScrappedAssets={props.showScrappedAssets ?? false}
        />
      </Box>
    </Box>
  );
}
